import type { FlowBillingScheduleStatus } from '../../../types/flowBilling'

/**
 * When the report will next be generated, and what it will cover.
 *
 * The schedule lives on the configuration — generation day, hour and minute,
 * read in the reporting timezone — so the page has to say what that adds up
 * to. "1st at 12:00" is a rule; "1 Sep 2026 12:00, covering August 2026" is
 * the thing an administrator actually wants to check before saving.
 *
 * Everything here is wall-clock arithmetic in the reporting zone. The only
 * conversion is reading "now" in that zone; after that the day, hour and
 * minute are the district's own, which is what the job compares against.
 */

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

export interface ScheduleConfig {
  timezone: string
  periodType: 'previous_month' | 'rolling_days'
  periodDays: number
  generationDay: number
  generationHour: number
  generationMinute: number
}

function nowInZone(timeZone: string, now: Date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(now)
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value)
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour') % 24, minute: get('minute') }
}

/**
 * The next generation moment, as local wall-clock parts.
 *
 * "Already past this month" rolls to the next month rather than to the same
 * day next year, which is the mistake a naive comparison makes in December.
 */
export function nextRunAt(config: ScheduleConfig, now = new Date()) {
  const local = nowInZone(config.timezone, now)
  const passed =
    local.day > config.generationDay ||
    (local.day === config.generationDay &&
      (local.hour > config.generationHour ||
        (local.hour === config.generationHour && local.minute >= config.generationMinute)))

  const month = passed ? (local.month === 12 ? 1 : local.month + 1) : local.month
  const year = passed && local.month === 12 ? local.year + 1 : local.year

  return {
    year,
    month,
    day: config.generationDay,
    hour: config.generationHour,
    minute: config.generationMinute,
  }
}

/**
 * The month a run started now would cover: the previous calendar month, named
 * in the reporting zone. Used to recognise that a period has already been sent
 * before offering to send it again.
 */
export function previousMonthLabel(timeZone: string, now = new Date()): string {
  const { year, month } = previousMonth(timeZone, now)
  return `${MONTHS[month - 1]} ${year}`
}

/** The last closed calendar month in the reporting zone. */
export function previousMonth(timeZone: string, now = new Date()): { year: number; month: number } {
  const local = nowInZone(timeZone, now)
  return local.month === 1
    ? { year: local.year - 1, month: 12 }
    : { year: local.year, month: local.month - 1 }
}

/** "September 2026". */
export function monthLabel({ year, month }: { year: number; month: number }): string {
  return `${MONTHS[month - 1]} ${year}`
}

/** One month either side, rolling over the year. */
export function shiftMonth(
  { year, month }: { year: number; month: number },
  by: number
): { year: number; month: number } {
  const index = year * 12 + (month - 1) + by
  return { year: Math.floor(index / 12), month: (index % 12) + 1 }
}

const pad = (n: number) => String(n).padStart(2, '0')

/** "1 Sep 2026 12:00 · period August 2026" — blank if the zone is unusable. */
export function describeNextRun(config: ScheduleConfig, now = new Date()): string {
  let at
  try {
    at = nextRunAt(config, now)
  } catch {
    // An unknown IANA zone throws in Intl. The schedule card is not the place
    // to surface that; the timezone field's own validation is.
    return ''
  }

  const when = `${at.day} ${SHORT_MONTHS[at.month - 1]} ${at.year} ${pad(at.hour)}:${pad(at.minute)}`

  if (config.periodType === 'rolling_days') {
    return `${when} · period the ${config.periodDays} days to that date`
  }

  // The previous calendar month, relative to the run — which in January is
  // December of the year before.
  const periodMonth = at.month === 1 ? 12 : at.month - 1
  const periodYear = at.month === 1 ? at.year - 1 : at.year
  return `${when} · period ${MONTHS[periodMonth - 1]} ${periodYear}`
}

/**
 * What the page should add to "Next run" about the period now due.
 *
 * "Next run" is arithmetic on the schedule, so once this month's time has
 * passed it already names next month — true of the schedule, and misleading
 * when this month's report has not actually gone out. The status comes from
 * the server's own checks; this only words it. Null when there is nothing to
 * add: switched off, not yet due, or already sent.
 */
export function describeScheduleStatus(
  status: FlowBillingScheduleStatus | undefined
): { tone: 'warning' | 'danger'; text: string } | null {
  if (!status || !('periodLabel' in status) || status.state === 'done') return null

  if (status.state === 'gave_up') {
    return {
      tone: 'danger',
      text:
        `${status.periodLabel} has not been sent. All ${status.maxAttempts} attempts failed and it will not ` +
        'be retried: fix the cause, then generate and send it from the Preview tab.',
    }
  }

  const retry =
    status.attempts > 0
      ? ` Attempt ${status.attempts} of ${status.maxAttempts} failed; the next is`
      : ' It is'
  return {
    tone: 'warning',
    text: `${status.periodLabel} has not been sent yet.${retry} due within a minute.`,
  }
}
